import * as Yup from 'yup'
import { differenceInYears, parse } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { checkEmailExists } from '../../services/user'

interface ValidatorContext {
  originalEmail: string
}

const useSettingsValidators = (
  currentUserId: string,
  originalEmail: string,
) => {
  const { t } = useTranslation()

  const schema = Yup.object().shape({
    birthday: Yup.string()
      .required(t('validation-birthday-required'))
      .test('is-valid-date', t('validation-birthday-typeError'), value => {
        if (!value) return false
        const parsedDate = parse(value, 'dd-MM-yyyy', new Date())
        return !isNaN(parsedDate.getTime())
      })
      .test('is-adult', t('validation-birthday-isAdult'), value => {
        if (!value) return false
        const today = new Date()
        const birthDate = parse(value, 'dd-MM-yyyy', new Date())
        return differenceInYears(today, birthDate) >= 18
      }),

    nickname: Yup.string()
      .required(t('validation-nickname-required'))
      .min(3, t('validation-nickname-min'))
      .max(20, t('validation-nickname-max')),

    email: Yup.string()
      .email(t('validation-email-invalid'))
      .required(t('validation-email-required'))
      .test('is-unique', t('email-already-taken'), async function (value) {
        if (!value) return false
        if (value === originalEmail) {
          return true
        }
        try {
          const isTaken = await checkEmailExists(value, currentUserId)
          return !isTaken
        } catch (error) {
          return false
        }
      }),

    phone: Yup.string()
      .nullable()
      .matches(/^\+?[1-9]\d{1,14}$/, t('invalid-phone-number')),

    address: Yup.object().shape({
      street: Yup.string().nullable(),
      number: Yup.string().nullable(),
      city: Yup.string().required(t('validation-city-required')),
      zip_code: Yup.string().required(t('validation-zipcode-required')),
    }),

    tva: Yup.string()
      .nullable()
      .transform(value => (value === '' ? null : value))
      .test('vat-format', t('validation-vat-format'), value => {
        if (!value) return true
        return /^[A-Z]{2}[0-9]{10}$/.test(value)
      }),
    status: Yup.array()
      .min(1, t('validation-status-required'))
      .required(t('validation-status-required')),
  })

  return schema
}

export default useSettingsValidators
